import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { ConflictError } from '../../api/errors';
import { shipmentsApi } from '../../api/shipments-api';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  linePicked,
  requirementLabel,
  type FulfillmentOrder,
  type FulfillmentPick,
  type HandoverMethod,
} from '../../domain/fulfillment';
import { fulfillmentErrorMessage, isUnknownOutcome, unavailableItemIds } from './error-message';
import type { FulfillmentReferences } from './use-fulfillment-references';

function Dialog({
  title,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}) {
  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <form
        className="confirmation-dialog form-surface"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={onSubmit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={`Close ${title.toLowerCase()} dialog`}
          >
            ×
          </button>
        </div>
        {children}
      </form>
    </div>
  );
}

/**
 * Error, conflict and unknown-outcome states shared by the order dialogs.
 * A conflict offers a reload; a network failure never pretends success.
 */
function useOrderMutation(
  run: () => Promise<FulfillmentOrder>,
  onDone: (order: FulfillmentOrder) => void,
  fallback: string,
) {
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [unknown, setUnknown] = useState(false);
  const mutation = useMutation({
    mutationFn: run,
    onSuccess: onDone,
    onError: (failed: unknown) => {
      if (isUnknownOutcome(failed)) setUnknown(true);
      else {
        setFailure(failed);
        setConflict(failed instanceof ConflictError);
        setError(fulfillmentErrorMessage(failed, fallback));
      }
    },
  });
  return {
    mutation,
    failure,
    reset: () => {
      setError(null);
      setFailure(null);
      setConflict(false);
      setUnknown(false);
    },
    status: (onReload: () => void): ReactNode => (
      <>
        {error ? (
          <div role="alert" className="form-error">
            {error}
            {conflict ? (
              <button type="button" className="link-button" onClick={onReload}>
                Reload order
              </button>
            ) : null}
          </div>
        ) : null}
        {unknown ? <PartialFailureNotice onCheckCurrentState={onReload} /> : null}
      </>
    ),
  };
}

export function CancelOrderDialog({
  order,
  onClose,
  onDone,
  onReload,
}: {
  order: FulfillmentOrder;
  onClose: () => void;
  onDone: (order: FulfillmentOrder) => void;
  onReload: () => void;
}) {
  const [reason, setReason] = useState('');
  const cancel = useOrderMutation(
    () => fulfillmentApi.cancelOrder(order.id, order.version, reason.trim()),
    onDone,
    'The order could not be cancelled.',
  );
  const picked = order.picks.filter((pick) => !pick.removed).length;
  return (
    <Dialog
      title="Cancel order"
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        cancel.reset();
        cancel.mutation.mutate();
      }}
    >
      <div className="warning-callout">
        Cancelling releases the reserved stock{picked > 0 ? ` and the ${picked} picked items` : ''}.
        The goods go back on the shelf. A cancelled order can’t be reopened.
      </div>
      <label>
        Reason (required)
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          required
        />
      </label>
      {cancel.status(onReload)}
      <div className="drawer-actions">
        <button type="button" onClick={onClose}>
          Keep order
        </button>
        <button
          type="submit"
          className="button-danger"
          disabled={!reason.trim() || cancel.mutation.isPending}
        >
          {cancel.mutation.isPending ? 'Cancelling…' : 'Cancel order'}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * A Remove button for each picked item a handover refused, so the picker
 * fixes the order where they stand (tidaltech/koi#8467).
 */
function RefusedPicks({
  order,
  failure,
  onRemove,
}: {
  order: FulfillmentOrder;
  failure: unknown;
  onRemove: (pick: FulfillmentPick) => void;
}) {
  const ids = unavailableItemIds(failure);
  const refused = order.picks.filter(
    (pick) => !pick.removed && pick.itemId !== undefined && ids.includes(pick.itemId),
  );
  if (refused.length === 0) return null;
  return (
    <ul className="refused-picks" aria-label="Items that can't ship">
      {refused.map((pick) => (
        <li key={pick.id}>
          <span className="mono">{pick.itemId}</span>
          <button type="button" className="link-button" onClick={() => onRemove(pick)}>
            Remove {pick.itemId}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function RemovePickDialog({
  order,
  pick,
  onClose,
  onDone,
  onReload,
}: {
  order: FulfillmentOrder;
  pick: FulfillmentPick;
  onClose: () => void;
  onDone: (order: FulfillmentOrder) => void;
  onReload: () => void;
}) {
  const [reason, setReason] = useState('');
  const remove = useOrderMutation(
    () => fulfillmentApi.removePick(order.id, pick.id, reason.trim()),
    onDone,
    'The pick could not be removed.',
  );
  const what = pick.itemId ?? `${pick.quantity} × ${pick.code}`;
  return (
    <Dialog
      title={`Remove ${what}`}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        remove.reset();
        remove.mutation.mutate();
      }}
    >
      <p>Put {what} back on the shelf. The removal is recorded with your name.</p>
      <label>
        Reason
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="e.g. wrong unit"
        />
      </label>
      {remove.status(onReload)}
      <div className="drawer-actions">
        <button type="button" onClick={onClose}>
          Keep pick
        </button>
        <button type="submit" className="button-danger" disabled={remove.mutation.isPending}>
          {remove.mutation.isPending ? 'Removing…' : 'Remove pick'}
        </button>
      </div>
    </Dialog>
  );
}

export function HandoverDialog({
  order,
  references,
  onClose,
  onDone,
  onReload,
  onRemovePick,
}: {
  order: FulfillmentOrder;
  references: FulfillmentReferences;
  onClose: () => void;
  onDone: (order: FulfillmentOrder) => void;
  onReload: () => void;
  /** Opens the remove dialog for a pick the handover refused. */
  onRemovePick?: (pick: FulfillmentPick) => void;
}) {
  const [recipient, setRecipient] = useState('');
  const [method, setMethod] = useState<HandoverMethod>('CARRIER');
  const [shipmentId, setShipmentId] = useState(order.shipmentId ?? '');
  const technician = method === 'TECHNICIAN';
  const shipments = useQuery({
    queryKey: ['shipments'],
    queryFn: () => shipmentsApi.listShipments(),
  });
  const handover = useOrderMutation(
    () =>
      fulfillmentApi.completeOrder(
        order.id,
        order.version,
        recipient.trim(),
        technician ? undefined : shipmentId || undefined,
        method,
      ),
    onDone,
    'Packing could not be finished.',
  );
  const itemCount = order.lines.reduce((total, line) => total + line.itemIds.length, 0);
  const shipmentOptions = [
    { value: '', label: 'No shipment yet' },
    ...(shipments.data?.shipments ?? [])
      .filter((shipment) => shipment.state === 'PENDING' || shipment.id === order.shipmentId)
      .sort((a, b) => b.shippedAt.localeCompare(a.shippedAt))
      .map((shipment) => ({
        value: shipment.id,
        label: `${shipment.cargonizerConsignmentNumber || shipment.id.slice(0, 8)} · ${shipment.destinationCustomer} · ${new Date(shipment.shippedAt).toLocaleDateString()}`,
      })),
  ];
  return (
    <Dialog
      title="Finish packing"
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        handover.reset();
        handover.mutation.mutate();
      }}
    >
      <p>Check the goods against the list before you finish packing.</p>
      <ul className="handover-summary">
        {order.lines.map((line) => (
          <li key={line.id}>
            <strong>
              {linePicked(line)} ×{' '}
              {requirementLabel(line.requirement, {
                part: references.partName,
                stock: references.stockName,
              })}
            </strong>
            {line.itemIds.length > 0 ? (
              <span className="mono"> {line.itemIds.join(', ')}</span>
            ) : null}
            {line.requirement.type === 'STOCK' ? (
              <span> from {references.locationName(line.locationId)}</span>
            ) : null}
          </li>
        ))}
      </ul>
      <fieldset className="plain-fieldset">
        <legend>How is it leaving?</legend>
        <label className="radio-field">
          <input
            type="radio"
            name="handover-method"
            checked={!technician}
            onChange={() => setMethod('CARRIER')}
          />
          With a carrier
        </label>
        <label className="radio-field">
          <input
            type="radio"
            name="handover-method"
            checked={technician}
            onChange={() => setMethod('TECHNICIAN')}
            disabled={Boolean(order.shipmentId)}
          />
          A technician takes it to the site
        </label>
      </fieldset>
      <div className="warning-callout">
        Finishing packing takes the stock off the shelf and assigns the {itemCount} serialized items
        to {references.siteName(order.siteId)} as {technician ? 'Delivered' : 'Ready to ship'}. It
        can’t be undone.
      </div>
      <label>
        {technician ? 'Technician (required)' : 'Handed to (optional)'}
        <input
          value={recipient}
          onChange={(event) => setRecipient(event.target.value)}
          placeholder={
            technician ? 'Technician name' : 'e.g. the driver; the carrier is on the shipment'
          }
          required={technician}
        />
      </label>
      {technician ? null : (
        <label>
          Shipment
          <SearchableSelect
            ariaLabel="Shipment"
            value={shipmentId}
            onChange={setShipmentId}
            options={shipmentOptions}
            placeholder="No shipment yet"
          />
        </label>
      )}
      {handover.status(onReload)}
      {onRemovePick ? (
        <RefusedPicks order={order} failure={handover.failure} onRemove={onRemovePick} />
      ) : null}
      <div className="drawer-actions">
        <button type="button" onClick={onClose}>
          Not yet
        </button>
        <button
          type="submit"
          className="primary"
          disabled={(technician && !recipient.trim()) || handover.mutation.isPending}
        >
          {handover.mutation.isPending ? 'Finishing…' : 'Finish packing'}
        </button>
      </div>
    </Dialog>
  );
}
